import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Paper, Box } from '@mui/material';
import styled from "styled-components";
import { useDownloadExcel } from 'react-export-table-to-excel';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

const BankStateReport = () => {
    const location = useLocation();
    const PFTableRef = useRef(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName } = getCompanyDisplayFromDetails(companyDetails || {});

    const {empDetails} = location.state;
    const month = empDetails[0]?.payroll_month || '';
    const year = empDetails[0]?.payroll_year || '';
    
    const empFilteredDetails = empDetails.map((empDetail, index) => {
        return({
            Sr_No : index,
            Employee_Code: empDetail.employee_code,
            Employee_Name: `${empDetail.first_name || ''} ${empDetail.last_name || ''}`.trim(),
            Account_Number: empDetail.ICICI_account_no,
            Net_Pay: empDetail.net_pay,
        });
    });
    let total_pay = 0;

    const { onDownload } = useDownloadExcel({
        currentTableRef : PFTableRef.current,
        filename : `${getShortCompanyName(companyName)}ReportforBankStatement${month.substring(0, 3)}${year}`,
        sheet:'sheet'
    })

    // Helper function to clean bank account numbers for display (removes all quotes)
    const cleanDisplayValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        // Convert to string, remove ALL single quotes (anywhere in the string), and trim whitespace
        let cleaned = String(value);
        // Remove all single quotes
        cleaned = cleaned.replace(/'/g, '');
        // Remove any other quote-like characters
        cleaned = cleaned.replace(/[''""]/g, '');
        cleaned = cleaned.trim();
        return cleaned;
    };

    // Effect to ensure bank account cells are always clean (no quotes) in the UI
    useEffect(() => {
        const cleanCells = () => {
            if (!PFTableRef.current) return;
            const bankAccountCells = PFTableRef.current.querySelectorAll('td[data-bank-account="true"]');
            bankAccountCells.forEach((cell) => {
                let cellText = cell.textContent || cell.innerText || '';
                // Remove ALL quotes and quote-like characters
                const cleanValue = cellText.replace(/'/g, '').replace(/[''""]/g, '').trim();
                if (cellText !== cleanValue) {
                    cell.textContent = cleanValue;
                    cell.innerHTML = cleanValue;
                }
            });
        };
        
        // Clean immediately and multiple times to catch any delayed updates
        cleanCells();
        const timer1 = setTimeout(cleanCells, 50);
        const timer2 = setTimeout(cleanCells, 100);
        const timer3 = setTimeout(cleanCells, 300);
        const timer4 = setTimeout(cleanCells, 500);
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
            clearTimeout(timer4);
        };
    }, [empFilteredDetails, forceUpdate]);

    // Custom download handler - formats bank account numbers as text for Excel (prevents scientific notation)
    const handleExcelDownload = () => {
        if (!PFTableRef.current) return;
        
        const table = PFTableRef.current;
        const bankAccountCells = table.querySelectorAll('td[data-bank-account="true"]');
        const originalValues = new Map();
        
        // Store original clean values and format for Excel as text (prevents scientific notation)
        bankAccountCells.forEach((cell) => {
            let cellText = cell.textContent || cell.innerText || '';
            // Remove any existing prefixes/formulas to get clean value
            const cleanValue = cellText.replace(/'/g, '').replace(/^[\s\u00A0\uFEFF\t]+/, '').replace(/^="(.+)"$/, '$1').trim();
            originalValues.set(cell, cleanValue);
            
            // Use zero-width non-breaking space (U+FEFF) - completely invisible, Excel treats as text
            // This prevents Excel from converting long numbers to scientific notation
            if (cleanValue) {
                cell.textContent = '\uFEFF' + cleanValue;
            }
        });
        
        // Trigger the download
        onDownload();
        
        // Restore clean values immediately after export
        setTimeout(() => {
            originalValues.forEach((cleanValue, cell) => {
                cell.textContent = cleanValue;
                cell.innerHTML = cleanValue;
            });
            setForceUpdate(prev => prev + 1);
        }, 100);
    };
    
    return (
        <Box sx={{display: "flex" ,justifyContent: "center", margin: "5x", padding: "20px", fontWeight: "500px"}}>
            <Paper elevation={5} sx={{width: "1500px", margin: 'auto',padding: '20px'}}>
                <Button
                    variant='contained'
                    color='success'
                    onClick={ handleExcelDownload }
                >
                    Download Bank Statement Report (excel)
                </Button>
                <TABLE ref = { PFTableRef } style={{width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{ width: 'auto', minWidth: '120px' }} />
                        <col style={{ width: 'auto', minWidth: '200px' }} />
                        <col style={{ width: 'auto', minWidth: '180px' }} />
                        <col style={{ width: 'auto', minWidth: '140px' }} />
                    </colgroup>
                    <tbody>
                        <tr>
                            <TH colSpan={4} style={{textAlign: 'center', padding: '8px'}}>Bank Statement details for {month} {year}</TH>
                        </tr>
                        <tr>
                            <TH style={{padding: '8px'}}>Employee Code</TH>
                            <TH style={{padding: '8px'}}>Employee Name</TH>
                            <TH style={{padding: '8px'}}>Account Number</TH>
                            <TH style={{padding: '8px', textAlign: 'right'}}>Net Pay</TH>
                        </tr>
                        {empFilteredDetails.map(emp => {
                            total_pay += emp.Net_Pay;
                            return(
                                <tr key={emp.Sr_No}>
                                    <TD style={{padding: '8px'}}>{emp.Employee_Code}</TD>
                                    <TD style={{textAlign: 'left', padding: '8px'}}>{emp.Employee_Name}</TD>
                                    <TD data-bank-account="true" style={{padding: '8px'}}>{cleanDisplayValue(emp.Account_Number)}</TD>
                                    <TD style={{textAlign: 'right', padding: '8px'}}>{emp.Net_Pay}</TD>
                                </tr>)
                            }
                        )}
                        <tr>
                            <TD style={{padding: '8px'}}></TD>
                            <TD style={{padding: '8px'}}></TD>
                            <TD style={{padding: '8px'}}><b>Total Pay</b></TD>
                            <TD style={{textAlign: 'right', padding: '8px'}}><b>{total_pay}</b></TD>
                        </tr>
                    </tbody>
                </TABLE>
            </Paper>
        </Box>
    );
}

export default BankStateReport;
const TABLE = styled.table`
    border: 1px solid;
`;
const TH = styled.th`
    border: 1px solid;
`;
const TD = styled.td`
    border: 1px solid;
`;
